import { useRef } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import LeadForm from '../components/eclora/LeadForm';
import { MaskedLine } from '../components/eclora/Reveal';

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
    const tiltRef = useRef(null);
    const sectionRef = useRef(null);
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 120, damping: 16 });
    const sry = useSpring(ry, { stiffness: 120, damping: 16 });

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

    const onMouseMove = (e) => {
        const rect = tiltRef.current?.getBoundingClientRect();
        if (!rect) return;
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        ry.set(px * 8);
        rx.set(-py * 8);
    };

    const onMouseLeave = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <section ref={sectionRef} className="relative overflow-hidden" data-testid="hero-section">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 right-[-20%] h-[36rem] w-[36rem] rounded-full bg-nude/25 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-[-25%] h-[28rem] w-[28rem] rounded-full bg-champagne/15 blur-3xl"
            />

            <header className="relative z-10 flex justify-center px-6 pt-8">
                <motion.img
                    src="/assets/logo-cream.jpg"
                    alt="Éclora — Il n'est jamais trop tard pour éclore"
                    className="w-28 mix-blend-multiply sm:w-32"
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE }}
                    data-testid="nav-logo"
                />
            </header>

            <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-10 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:pb-28 lg:pt-16">
                <div className="text-center lg:text-left">
                    <motion.p
                        className="eyebrow"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.15 }}
                    >
                        Reset Éclora
                    </motion.p>

                    <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-bordeaux sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.25}>7 jours pour</MaskedLine>
                        <MaskedLine delay={0.4}>commencer ton</MaskedLine>
                        <MaskedLine delay={0.55}>
                            <span className="relative inline-block text-serifit">
                                nouveau chapitre.
                                <motion.svg
                                    viewBox="0 0 220 12"
                                    className="absolute -bottom-2 left-0 w-full"
                                    initial={{ pathLength: 0 }}
                                    aria-hidden="true"
                                >
                                    <motion.path
                                        d="M4 8 C 60 2, 150 2, 216 7"
                                        fill="none"
                                        stroke="#C6A46A"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 1, delay: 1.5, ease: EASE }}
                                    />
                                </motion.svg>
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        className="mt-7 font-script text-2xl leading-snug text-cacao sm:text-3xl"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.95, ease: EASE }}
                    >
                        Tu sens que quelque chose doit changer, mais tu ne sais pas forcément par où commencer ?
                    </motion.p>

                    <motion.p
                        className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-cacao/85 lg:mx-0"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.1, ease: EASE }}
                    >
                        Reset Éclora est une expérience guidée de 7 jours pour faire le point, clarifier ce que tu veux
                        et poser tes premières actions vers la femme que tu veux devenir.
                    </motion.p>

                    <motion.div
                        className="mx-auto mt-8 max-w-md lg:mx-0"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.25, ease: EASE }}
                    >
                        <LeadForm
                            source="hero"
                            idPrefix="hero"
                            footnote="Reçois gratuitement ton Reset Éclora par email."
                        />
                    </motion.div>
                </div>

                <motion.div style={{ y: parallaxY }} className="relative mx-auto w-full max-w-sm lg:max-w-none">
                    <motion.div
                        initial={{ opacity: 0, y: 40, rotate: 2 }}
                        animate={{ opacity: 1, y: 0, rotate: 0 }}
                        transition={{ duration: 1.3, delay: 0.7, ease: EASE }}
                        style={{ perspective: 1000 }}
                        className="relative"
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -inset-6 rounded-full border border-champagne/40 sm:-inset-10"
                        />
                        <motion.div
                            ref={tiltRef}
                            onMouseMove={onMouseMove}
                            onMouseLeave={onMouseLeave}
                            style={{ rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d' }}
                            className="relative shadow-[0_40px_80px_-30px_rgba(114,47,55,0.45)]"
                        >
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                <img
                                    src="/assets/cover.jpg"
                                    alt="Couverture du workbook Reset Éclora — 7 jours pour te reconnecter à la femme que tu veux devenir"
                                    className="w-full border border-champagne/30"
                                    loading="eager"
                                    data-testid="hero-workbook-cover"
                                />
                            </motion.div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 1.5, ease: EASE }}
                            className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap border border-champagne/60 bg-creme/95 px-5 py-2.5 backdrop-blur-sm"
                            data-testid="hero-workbook-badge"
                        >
                            <span className="font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-bordeaux sm:text-[11px]">
                                Gratuit ✦ 7 jours ✦ 15 min/jour
                            </span>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
